import { Shield } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { DimensionCard } from "./DimensionCard";
import { MetricCard } from "./MetricCard";
import type { DailyTeamStats } from "../api/useTeamStats";

interface SafetySectionProps {
  daily: DailyTeamStats[];
}

function computeDeltas(daily: DailyTeamStats[]) {
  const mid = Math.floor(daily.length / 2);
  const recent = daily.slice(mid);
  const previous = daily.slice(0, mid);
  const sum = (arr: DailyTeamStats[], key: keyof DailyTeamStats) =>
    arr.reduce((s, d) => s + (d[key] as number), 0);

  return {
    breaches: sum(recent, "slaBreaches") - sum(previous, "slaBreaches"),
    escalations: sum(recent, "escalations") - sum(previous, "escalations"),
  };
}

export function SafetySection({ daily }: SafetySectionProps) {
  const totalBreaches = daily.reduce((s, d) => s + d.slaBreaches, 0);
  const totalEscalations = daily.reduce((s, d) => s + d.escalations, 0);
  const agedItems = daily.length > 0 ? (daily[daily.length - 1]?.totalItems ?? 0) : 0;
  const deltas = computeDeltas(daily);

  const chartData = daily.map((d) => ({
    date: new Date(d.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    breaches: d.slaBreaches,
  }));

  return (
    <DimensionCard title="Safety" icon={Shield} colorTheme="red" id="safety">
      <div className="grid grid-cols-3 gap-3 mb-4">
        <MetricCard
          label="SLA Breaches"
          value={totalBreaches}
          delta={{ value: deltas.breaches, label: "vs prior period" }}
        />
        <MetricCard label="Total Open (latest)" value={agedItems} />
        <MetricCard
          label="Escalations"
          value={totalEscalations}
          delta={{ value: deltas.escalations, label: "vs prior period" }}
        />
      </div>
      {chartData.length > 0 ? (
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ backgroundColor: "#1f2937", border: "none", borderRadius: "6px", color: "#f9fafb", fontSize: "12px" }} />
              <Line type="monotone" dataKey="breaches" stroke="#dc2626" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex items-center justify-center h-48 bg-gray-50 dark:bg-white/5 rounded-lg">
          <p className="text-sm text-gray-400">No trend data available</p>
        </div>
      )}
    </DimensionCard>
  );
}
